import {
  Card,
  CoherenceDataGrid,
  LoadingState,
  TDataGridColumn,
} from "@coherence-design-system/controls";
import { Stack } from "@fluentui/react/lib/Stack";
import {
  IGroup,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  SelectionMode,
} from "@fluentui/react";
import React, {
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  FunctionComponent,
  useContext,
} from "react";
import { QuotationDetailService } from "../../../services/Quotations/QuotationDetailService";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { QuotationContext } from "../QuotationContext";
import { IBundleItemEntity, QuotationInfoType } from "../Quotation.types";
import {
  IAuthDiscountAmounts,
  IDiscountedLineItem,
  IDiscountSummaryResponse,
} from "../QuotationDetail/QuotationDetail.types";
import {
  CustomSpanStyle,
  CustomSpanStyleWithWordBreak,
  stackTokensWithGap5,
} from "../QuotationStyle";
import {
  NO,
  ORDER_DETAILS,
  PRICING_SUMMARY,
  YES,
} from "../Common/QuotationData";
import { priceWithCurrency, priceWithCurrencyForJapan } from "../../../Utils/PriceWithCurrency";
import { DeviceType } from "../../../common/enum/DeviceTypes";
import { formattedPrice, formattedPriceForJapan } from "../../../Utils/FormattedPrice";

export interface IItemComponent {
  setQuotationMessage: Dispatch<SetStateAction<boolean>>;
  setQuotationErrorMessage: Dispatch<SetStateAction<boolean>>;
  zoomPercentage: number;
  windowSize: {
    width: number;
    height: number;
  };
}

let taxType = "";
const ItemComponent: FunctionComponent<IItemComponent> = (props) => {
  const appInsights = useAppInsightsContext();
  const [bundle, setBundle] = useState<IGroup[]>([]);
  const { quotationInfo } = useContext(QuotationContext) as QuotationInfoType;
  
  // Helper function to format price based on country
  const formatPriceByCountry = (price: string | number) => {
    return quotationInfo.customer?.country === "Japan" 
      ? formattedPriceForJapan(price) 
      : formattedPrice(price);
  };
  
  // Calculate Japan line item tax total by summing with decimal precision and only truncating the final result
  const calculateJapanLineTaxTotal = (): number => {
    if (quotationInfo.customer?.country !== "Japan") {
      return 0;
    }
    
    let totalLineTax = 0;
    
    // Process standalone items
    if (quotationInfo.standaloneItems && quotationInfo.standaloneItems.length > 0) {
      quotationInfo.standaloneItems.forEach((item: any) => {
        if (item.lineItemTax) {
          // Convert to number if it's a string and add full decimal value
          const lineItemTaxValue = typeof item.lineItemTax === 'string' 
            ? parseFloat(item.lineItemTax) 
            : item.lineItemTax;
          
          // Add the full decimal value to the total (no truncation per line)
          totalLineTax += lineItemTaxValue;
        }
      });
    }
    
    // Process bundle items
    if (quotationInfo.bundleItems && quotationInfo.bundleItems.length > 0) {
      quotationInfo.bundleItems.forEach((item: any) => {
        if (item.lineItemTax) {
          // Convert to number if it's a string and add full decimal value
          const lineItemTaxValue = typeof item.lineItemTax === 'string' 
            ? parseFloat(item.lineItemTax) 
            : item.lineItemTax;
          
          // Add the full decimal value to the total (no truncation per line)
          totalLineTax += lineItemTaxValue;
        }
      });
    }
    
    // Only truncate the final sum for display
    return Math.floor(totalLineTax);
  };
  
  // Calculate Japan line total amount using quotationInfo.total instead of summing individual line totals
  const calculateJapanLineTotalAmount = (): number => {
    if (quotationInfo.customer?.country !== "Japan") {
      return 0;
    }
    
    const totalValue = typeof quotationInfo.total === 'string'
      ? parseFloat(quotationInfo.total)
      : quotationInfo.total;
    
    if (Number.isNaN(totalValue)) {
      console.error("Invalid total value for Japan line total calculation:", quotationInfo.total);
      return 0;
    }
    
    return Math.floor(totalValue);
  };

  // Format line tax total without decimal places using formattedPriceForJapan
  const formatLineTaxTotalWithoutDecimals = (currency: string, price: number): string => {
    // First import priceWithCurrencyForJapan if needed
    // Use priceWithCurrencyForJapan which uses formattedPriceForJapan internally
    // This will format the number without decimals (.00)
    return priceWithCurrencyForJapan(currency, Math.floor(price));
  };
  
  // Function to handle special rendering for totals row
  const handleTotalsRowLineItemTax = (item: any) => {
    // Check if this is the totals row by looking for common patterns in the DataGrid footer cell
    const isTotalsRow = 
      !item || 
      (item && typeof item.lineItemTax === 'undefined') || 
      (item && item.productTitle === "Total") ||
      (item && item.title === "Total") ||
      (item && item.skuId === "total");
    
    if (isTotalsRow) {
      // For Japan, show our custom calculated tax total
      if (quotationInfo.customer?.country === "Japan") {
        const japanTaxTotal = calculateJapanLineTaxTotal();
        console.log("Japan Tax Total for Totals Row:", japanTaxTotal); // Debugging
        return (
          <Stack verticalFill verticalAlign="center" horizontalAlign="end">
            <span style={{
              ...CustomSpanStyle,
            }}>
              {formatLineTaxTotalWithoutDecimals(quotationInfo.quoteCurrency, japanTaxTotal)}
            </span>
          </Stack>
        );
      }
    }
    
    // For regular rows or non-Japan, return null to use default rendering
    return null;
  };
  
  // Use MutationObserver to reliably update Japan tax total in the totals row
  useEffect(() => {
    // Only run this for Japan
    if (quotationInfo.customer?.country === "Japan") {
      let observer: MutationObserver | null = null;
      
      // Function to update the Japan tax total in the grid's totals row
      const updateJapanTaxTotalInGrid = () => {
        try {
          // Calculate Japan tax total first
          const japanTaxTotal = calculateJapanLineTaxTotal();
          console.log("Calculated Japan Tax Total:", japanTaxTotal);
          
          // Get all rows in the grid
          const rows = document.querySelectorAll('[role="row"]');
          
          // Find the totals row
          let totalsRow: Element | null = null;
          rows.forEach((row: Element) => {
            const firstCell = row.querySelector('[role="gridcell"]:first-child');
            if (firstCell && firstCell.textContent && firstCell.textContent.trim().includes("Total")) {
              totalsRow = row;
            }
          });
          
          if (totalsRow) {
            // Find the Line Tax cell in the totals row using a more straightforward approach
            // Type assertion to HTMLElement
            const htmlTotalsRow = totalsRow as HTMLElement;
            const cells = htmlTotalsRow.querySelectorAll('[role="gridcell"]');
            let lineTaxCell: HTMLElement | null = null;
            
            // Convert NodeList to array and iterate
            Array.from(cells).forEach((cell) => {
              const htmlCell = cell as HTMLElement;
              const columnKey = htmlCell.getAttribute('data-automation-key') || '';
              if (columnKey.includes("Line Tax") || 
                  columnKey.includes("lineItemTax") ||
                  columnKey === "Line Tax (Extended Price * Tax rate)") {
                lineTaxCell = htmlCell;
              }
            });
            
            if (lineTaxCell) {
              console.log("Found Line Tax cell in totals row");
              // Update the cell content with our calculated Japan tax total - styled like other totals and right-aligned
              const htmlContent = `<div style="height: 100%; display: flex; align-items: center; justify-content: flex-end; width: 100%;">
                <span style="
                  font-family: Segoe UI, Segoe UI Web (West European), Segoe UI, -apple-system, BlinkMacSystemFont, Roboto, Helvetica Neue, sans-serif;
                  line-height: 20px;
                  font-size: 14px;
                  font-weight: 400;
                  color: rgb(50, 49, 48);
                ">${formatLineTaxTotalWithoutDecimals(quotationInfo.quoteCurrency, japanTaxTotal)}</span>
              </div>`;
              
              // Type assertion to HTMLElement
              const htmlElement = lineTaxCell as HTMLElement;
              htmlElement.innerHTML = htmlContent;
              // htmlElement.style.backgroundColor = '#f0f8ff'; 
              // htmlElement.style.borderBottom = '2px solid #0078d4';
            } else {
              console.log("Line Tax cell not found in totals row");
            }

            // Also update Line Total Amount cell with quotationInfo.total
            let lineTotalCell: HTMLElement | null = null;
            Array.from(cells).forEach((cell) => {
              const htmlCell = cell as HTMLElement;
              const columnKey = htmlCell.getAttribute('data-automation-key') || '';
              if (columnKey.includes("Line Total Amount") || 
                  columnKey.includes("lineTotal")) {
                lineTotalCell = htmlCell;
              }
            });
            
            if (lineTotalCell) {
              const japanLineTotalAmount = calculateJapanLineTotalAmount();
              const lineTotalHtmlContent = `<div style="height: 100%; display: flex; align-items: center; justify-content: flex-end; width: 100%;">
                <span style="
                  font-family: Segoe UI, Segoe UI Web (West European), Segoe UI, -apple-system, BlinkMacSystemFont, Roboto, Helvetica Neue, sans-serif;
                  line-height: 20px;
                  font-size: 14px;
                  font-weight: 400;
                  color: rgb(50, 49, 48);
                ">${formatLineTaxTotalWithoutDecimals(quotationInfo.quoteCurrency, japanLineTotalAmount)}</span>
              </div>`;
              
              const htmlLineTotalElement = lineTotalCell as HTMLElement;
              htmlLineTotalElement.innerHTML = lineTotalHtmlContent;
            }
          } else {
            console.log("Totals row not found");
          }
        } catch (e) {
          console.error("Error updating Japan tax total in grid:", e);
        }
      };
      
      // Set up a MutationObserver to watch for changes in the DOM
      observer = new MutationObserver((mutations) => {
        // Check if any mutation includes grid cells
        const hasGridCellChanges = mutations.some(mutation => {
          // Type assertion for Node
          const targetElement = mutation.target as HTMLElement;
          return targetElement && typeof targetElement.querySelectorAll === 'function'
            ? targetElement.querySelectorAll('[role="gridcell"]').length > 0
            : false;
        });
        
        if (hasGridCellChanges) {
          // Grid cells have changed, update our Japan tax total
          updateJapanTaxTotalInGrid();
        }
      });
      
      // Start observing the document with the configured parameters
      observer.observe(document.body, { 
        childList: true, 
        subtree: true, 
        attributes: true,
        attributeFilter: ['class', 'style', 'data-automation-key'] 
      });
      
      // Also run on initial render after a short delay
      setTimeout(updateJapanTaxTotalInGrid, 500);
      
      // Clean up the observer on component unmount
      return () => {
        if (observer) {
          observer.disconnect();
        }
      };
    }
  }, [quotationInfo.standaloneItems, quotationInfo.bundleItems, quotationInfo.customer?.country]);
  
  // Add JapanTaxTotal display as a separate component after the grid
  const renderJapanTaxTotal = () => {
    if (quotationInfo.customer?.country !== "Japan") {
      return null;
    }
    
    const japanTaxTotal = calculateJapanLineTaxTotal();
    

  };

  // Add Japan Line Total Amount display as a separate component after the grid
  const renderJapanLineTotalAmount = () => {
    if (quotationInfo.customer?.country !== "Japan") {
      return null;
    }
    
    const japanLineTotalAmount = calculateJapanLineTotalAmount();
    

  };

  const [discountTableLoading, setDiscountTableLoading] =
    useState<boolean>(true);
  const [selectedPivot, setSelectedPivot] = useState<string>(ORDER_DETAILS);
  const [detailSelectedPivot, setDetailSelectedPivot] = useState<string>(
    quotationInfo.standaloneItems.length > 0
      ? DeviceType.Standalone
      : DeviceType.Bundle
  );
  const [discountSummary, setDiscountSummary] =
    useState<IDiscountSummaryResponse>();

  const customListConfig: TDataGridColumn<IDiscountedLineItem>[] = [
    {
      fieldName: "itemSkuId",
      maxWidth: 100,
      isResizable: true,
      key: "sku",
      name: "SKU",
      type: "string",
    },
    {
      fieldName: "productName",
      isResizable: true,
      isMultiline: true,
      key: "productName",
      name: "Product Name",
      type: "string",
    },
    {
      fieldName: "msrp",
      isResizable: true,
      key: "msrp",
      name: "",
      type: "number",
      customFooterTitle: "",
      showTotal: false,
      displayFullValue: true,
      onRender(item) {

        taxType = discountSummary?.discountedLineItems?.[0]?.taxType || "";
        return (
          <Stack verticalFill verticalAlign="center">
            <span style={CustomSpanStyle}>{formattedPrice(item.msrp)}</span>
          </Stack>
        );
      },
      onRenderHeader(
        props?: any,
        defaultRender?: (props?: any) => JSX.Element | null
      ) {
        const taxType = discountSummary?.discountedLineItems?.[0]?.taxType;

        return taxType ?
          (
            <div> MSRP Ex {taxType}
              {defaultRender && defaultRender(props)}
            </div>
          ) : (
            <div>MSRP
              {defaultRender && defaultRender(props)}
            </div>
          );

      },
    },
    {
      fieldName: "discountDetails",
      isResizable: true,
      key: "Discount Percentages",
      name: "Discount Percentages",
      type: "number",
      isUpdated: true,
      minColumnWidth: 140,
      onRender(item) {
        return (
          <Stack verticalFill verticalAlign="center">
            {item?.discountDetails?.map(
              (discount: IAuthDiscountAmounts, index: number) => {
                const { type, percent } = discount;
                return (
                  <Stack
                    horizontal
                    horizontalAlign="space-between"
                    key={index + 1}
                  >
                    <span
                      style={
                        type.length >= 10
                          ? CustomSpanStyleWithWordBreak
                          : CustomSpanStyle
                      }
                    >
                      {type}
                    </span>
                    <span style={CustomSpanStyle}>{percent}%</span>
                  </Stack>
                );
              }
            )}
          </Stack>
        );
      },
    },
    {
      fieldName: "totalLineDiscount",
      isResizable: true,
      key: "totalLineDiscount",
      name: "Total Discount",
      type: "number",
      customFooterTitle: "",
      showTotal: false,
      displayFullValue: true,
      minColumnWidth: 250,
      calculatedWidth: 300,
      onRender(item) {
        return (
          <Stack verticalFill verticalAlign="center">
            <Stack horizontal horizontalAlign="end" tokens={stackTokensWithGap5}>
              <span style={CustomSpanStyle}>{item.totalLineDiscount}%</span>
              <span style={CustomSpanStyle}>{item?.discPercentage && ` (${item.discPercentage})`}</span>
            </Stack>
          </Stack>
        );
      },
    },
    {
      fieldName: "quantity",
      isResizable: true,
      key: "quantity",
      name: "Quantity",
      type: "number",
      customFooterTitle: "",
      displayFullValue: true,
      isUpdated: true,
      onRender(item) {
        return (
          <Stack verticalFill verticalAlign="center">
            <span style={CustomSpanStyle}>{item.quantity}</span>
          </Stack>
        );
      },
    },
    {
      fieldName: "taxPercent",
      isResizable: true,
      key: "VAT",
      name: "",
      type: "number",
      customFooterTitle: "",
      displayFullValue: true,
      isUpdated: true,
      onRender(item) {
        return (
          <Stack verticalFill verticalAlign="center">
            <span style={CustomSpanStyle}>{item.taxPercent}%</span>
          </Stack>
        );
      },
      onRenderHeader(
        props?: any,
        defaultRender?: (props?: any) => JSX.Element | null
      ) {
        const taxType = discountSummary?.discountedLineItems?.[0]?.taxType;
        return taxType ? (
          <div>
            <span style={CustomSpanStyle}></span>
            {taxType}
            {defaultRender && defaultRender(props)}
          </div>
        ) : (
          <div>{defaultRender && defaultRender(props)}</div>
        );
      },
    },
    {
      fieldName: "feeAmount",
      isResizable: true,
      key: "levyFees",
      name: "Levy Fees",
      type: "number",
      customFooterTitle: "",
      displayFullValue: true,
      isUpdated: true,
      onRender(item) {
        return (
          <Stack verticalFill verticalAlign="center">
            <span style={CustomSpanStyle}>
              {formattedPrice(item.feeAmount)}
            </span>
          </Stack>
        );
      },
    },
    {
      fieldName: "discountDetails",
      isResizable: true,
      key: "lineDiscounts",
      name: "Line Discounts",
      type: "number",
      customFooterTitle: "",
      displayFullValue: true,
      isUpdated: true,
      minColumnWidth: 250,
      onRender(item) {
        const {
          discountAmountPerUnit,
          discountPricePerUnit,
          totalDiscountAmount,
          totalDiscountPrice,
        } = item.lineDiscounts || {};
        taxType = discountSummary?.discountedLineItems?.[0]?.taxType || "";
        const totalTaxAmount = item.feeAndTaxes
          ? item.feeAndTaxes.totalTaxAmount
          : 0;
        const totalPricePerItem = totalDiscountPrice + totalTaxAmount;
        return (
          <Stack
            verticalFill
            verticalAlign="center"
            tokens={stackTokensWithGap5}
          >
            <Stack horizontal horizontalAlign="space-between">
              <span style={CustomSpanStyle}>Discount amount per unit</span>
              <span style={CustomSpanStyle}>
                {formattedPrice(discountAmountPerUnit ?? 0)}
              </span>
            </Stack>
            <Stack horizontal horizontalAlign="space-between">
              <span style={CustomSpanStyle}>Discount price per unit</span>
              <span style={CustomSpanStyle}>
                {formattedPrice(discountPricePerUnit ?? 0)}
              </span>
            </Stack>
            <Stack horizontal horizontalAlign="space-between">
              <span style={CustomSpanStyle}>Total discount amount</span>
              <span style={CustomSpanStyle}>
                {formattedPrice(totalDiscountAmount ?? 0)}
              </span>
            </Stack>
            <Stack horizontal horizontalAlign="space-between">
              <span style={CustomSpanStyle}>Total discount price</span>
              <span style={CustomSpanStyle}>
                {formattedPrice(totalDiscountPrice ?? 0)}
              </span>
            </Stack>
            <Stack horizontal horizontalAlign="space-between">
              <span style={CustomSpanStyle}>Total discount price + {taxType}</span>
              <span style={CustomSpanStyle}>
                {formattedPrice(totalPricePerItem ?? 0)}
              </span>
            </Stack>
          </Stack>
        );
      },
    },
    {
      fieldName: "feeAndTaxes",
      isResizable: true,
      key: "lineVatAndFees",
      name: "",
      type: "number",
      customFooterTitle: "",
      displayFullValue: true,
      isUpdated: true,
      minColumnWidth: 200,
      onRender(item) {
        taxType = discountSummary?.discountedLineItems?.[0]?.taxType || "";
        const feePerUnit = item.feeAndTaxes ? item.feeAndTaxes.feePerUnit : 0;
        const taxPerUnit = item.feeAndTaxes ? item.feeAndTaxes.taxPerUnit : 0;
        const totalFeeAmount = item.feeAndTaxes
          ? item.feeAndTaxes.totalFeeAmount
          : 0;
        const totalTaxAmount = item.feeAndTaxes
          ? item.feeAndTaxes.totalTaxAmount
          : 0;
        return (
          <Stack
            verticalFill
            verticalAlign="center"
            tokens={stackTokensWithGap5}
          >
            <Stack horizontal horizontalAlign="space-between">
              <span style={CustomSpanStyle}>{taxType} Per Unit </span>
              <span style={CustomSpanStyle}>{formattedPrice(taxPerUnit)}</span>
            </Stack>
            {quotationInfo.customer.region === "EMEA" && (
              <Stack horizontal horizontalAlign="space-between">
                <span style={CustomSpanStyle}>Fee Per Unit</span>
                <span style={CustomSpanStyle}>
                  {formattedPrice(feePerUnit)}
                </span>
              </Stack>
            )}
            <Stack horizontal horizontalAlign="space-between">
              <span style={CustomSpanStyle}>Total {taxType} Amount</span>
              <span style={CustomSpanStyle}>
                {formattedPrice(totalTaxAmount)}
              </span>
            </Stack>
            {quotationInfo.customer.region === "EMEA" && (
              <Stack horizontal horizontalAlign="space-between">
                <span style={CustomSpanStyle}>Total Fees Amount</span>
                <span style={CustomSpanStyle}>
                  {formattedPrice(totalFeeAmount)}
                </span>
              </Stack>
            )}
          </Stack>
        );
      },
      onRenderHeader(
        props?: any,
        defaultRender?: (props?: any) => JSX.Element | null
      ) {
        const taxType = discountSummary?.discountedLineItems?.[0]?.taxType;
        return taxType ? (
          <div>
            {quotationInfo.customer.region === "EMEA"
              ? `Line ${taxType} & Fees`
              : `Line ${taxType}`}
            {defaultRender && defaultRender(props)}
          </div>
        ) : (
          <div>{defaultRender && defaultRender(props)}</div>
        );
      },
    },
  ];

  useEffect(() => {
    const groups: IGroup[] = quotationInfo.bundles.map(
      (item: IBundleItemEntity) => {
        const { bundleId, bundleTitle, count, startIndex, index } = item;
        const groupItem = {
          key: bundleId.concat(index.toString()),
          name: bundleTitle,
          startIndex,
          count,
          level: 0,
        };
        return groupItem;
      }
    );
    setBundle(groups);
  }, [JSON.stringify(quotationInfo.bundleItems)]);

  useEffect(() => {
    if (selectedPivot === PRICING_SUMMARY) {
      setDiscountTableLoading(true);
      const discountPayload = {
        quoteId: quotationInfo.quotationId,
        partitionKey: quotationInfo.partitionKey,
      };
      QuotationDetailService.getDiscountSummary(discountPayload)
        .then((res: any) => {
          if (res.data.code === 200) {
            setDiscountSummary(res.data.data);
            taxType = res.data.data?.discountedLineItems?.[0]?.taxType;
            setDiscountTableLoading(false);
          }
        })
        .catch((error) => {
          setDiscountTableLoading(false);
        });
    } else {
      setDetailSelectedPivot(
        selectedPivot === ORDER_DETAILS &&
          quotationInfo.standaloneItems.length > 0
          ? DeviceType.Standalone
          : DeviceType.Bundle
      );
    }
  }, [selectedPivot]);

  const onRenderDetailsHeader = (headerProps: any, defaultRender: any) => {
    if (!headerProps || !defaultRender) {
      return null;
    }
    return defaultRender({
      ...headerProps,
      styles: {
        root: {
          height: "70px",
          lineHeight: "70px",
          paddingBottom: "10px",
          selectors: {
            ".ms-DetailsHeader-cell": {
              whiteSpace: "normal",
              textOverflow: "clip",
              lineHeight: "normal",
              height: "60px",
              paddingBottom: "10px",
            },
            ".ms-DetailsHeader-cellTitle": {
              height: "120%",
              alignItems: "center",
            },
            ".ms-DetailsRow-cell.ms-DetailsRow-cellCheck": {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
          },
        },
      },
    });
  };

  const renderCustomRow = (props: any, defaultRender: any) => {
    const { dynamicDiscountAmount, discountedPercentage, isMPriceCalculated } = props.item;
    const govtAffiliated = quotationInfo.customer?.govtAffiliated;
    const warningMessage = !isMPriceCalculated ? `MSRP Ex ${props.item.taxType} not available for this item.` : '';
    const rowText =
      discountedPercentage > 100
        ? `Discount percentage is invalid. Change the discount percentage to bring it to 100% or less than 100%.`
        : dynamicDiscountAmount <= 0 && !govtAffiliated
          ? `There's no automated discount available.`
          : "";
    return (
      <Stack>
        {rowText && (
          <Stack>
            <MessageBar
              messageBarType={
                discountedPercentage > 100
                  ? MessageBarType.error
                  : MessageBarType.warning
              }
            >
              {rowText}
            </MessageBar>
          </Stack>
        )}
        {warningMessage && (
          <MessageBar messageBarType={MessageBarType.warning}>
            {warningMessage}
          </MessageBar>
        )}
        {defaultRender({
          ...props,
        })}
      </Stack>
    );
  };

  const calculateTotalsAndAverage = () => {
    const totals: Record<string, number> = {};
    let totalDiscountPercent = 0;

    quotationInfo.standaloneItems.forEach((item: any) => {
      Object.keys(item).forEach((key: any) => {
        if (key !== "discountPercent") {
          totals[key] = (totals[key] || 0) + (item[key] || 0);
        } else {
          totalDiscountPercent += item[key] || 0;
        }
      });
    });
    quotationInfo.bundleItems.forEach((item: any) => {
      Object.keys(item).forEach((key: any) => {
        if (key !== "discountPercent") {
          totals[key] = (totals[key] || 0) + (item[key] || 0);
        } else {
          totalDiscountPercent += item[key] || 0;
        }
      });
    });

    const totalItems =
      quotationInfo.standaloneItems.length + quotationInfo.bundleItems.length;
    const averageDiscountPercent = totalDiscountPercent / totalItems;

    return { totals, averageDiscountPercent };
  };
  const onDetailPivotSelectionChange = (
    item?: PivotItem,
    ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    if (item?.props.headerText) {
      appInsights.trackEvent({
        name: `In ItemComponent.tsx, Clicked Pivot ${item.props.headerText} to show items of selected type.`,
      });
      setDetailSelectedPivot(item.props.headerText);
    }
  };
  const renderPivots = () => {
    return (
      <Pivot
        aria-label={DeviceType.Standalone}
        onLinkClick={onDetailPivotSelectionChange}
      >
        {quotationInfo?.standaloneItems?.length && (
          <PivotItem headerText={DeviceType.Standalone}></PivotItem>
        )}
        {quotationInfo?.bundleItems?.length && (
          <PivotItem headerText={DeviceType.Bundle}></PivotItem>
        )}
      </Pivot>
    );
  };

  const renderStandaloneItemDetailsComponent = (): React.ReactElement => {
    return (
      <Card>
        <Stack>
          <div
            style={{
              height: "475px",
            }}
          >
            <CoherenceDataGrid
              ariaLabelForSelectAllCheckbox="select all checkbox"
              checkButtonAriaLabel="checkbox"
              compactMode="compact"
              data={quotationInfo.standaloneItems}
              onRenderDetailsHeader={onRenderDetailsHeader}
              onRenderRow={renderCustomRow}
              isScrollable
              styles={{
                root: {
                  selectors: {
                    'div[role="gridcell"].discount_pr > div[aria-live="off"] > span::after': {
                      content: '"%"',
                    }
                  }
                }
              }}
              listConfig={[
                {
                  fieldName: "sapItemId",
                  maxWidth: 100,
                  isResizable: true,
                  key: "skuId",
                  name: "SKU",
                  type: "string",
                },
                {
                  fieldName: "productTitle",
                  isResizable: true,
                  isMultiline: true,
                  key: "productName",
                  name: "Product Name",
                  type: "string",
                },
                {
                  fieldName: "availableInventory",
                  isResizable: true,
                  key: "availableInventory",
                  name: "Available Inventory",
                  type: "number",
                  customFooterTitle: "",
                  showTotal: false,
                  displayFullValue: true,
                  minColumnWidth: 150,
                },
                {
                  fieldName: "quantity",
                  isResizable: true,
                  key: "quantity",
                  name: "Quantity",
                  type: "number",
                  customFooterTitle: "",
                  displayFullValue: true,
                },
                {
                  fieldName: "msrp",
                  isResizable: true,
                  key: "msrp",
                  name: "",
                  type: "number",
                  customFooterTitle: "",
                  showTotal: false,
                  displayFullValue: true,
                  onRender(item) {
                    taxType = quotationInfo.standaloneItems?.[0]?.taxType || "";
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.msrp)}
                        </span>
                      </Stack>
                    );
                  },
                  onRenderHeader(
                    props?: any,
                    defaultRender?: (props?: any) => JSX.Element | null
                  ) {
                    //Standalone

                    const taxType = quotationInfo.standaloneItems?.[0]?.taxType;
                    return taxType ?
                      (
                        <div> MSRP Ex {taxType}
                          {defaultRender && defaultRender(props)}
                        </div>
                      ) : (
                        <div>MSRP
                          {defaultRender && defaultRender(props)}
                        </div>
                      );

                  },

                },
                {
                  fieldName: "discountedPercentage",
                  isResizable: true,
                  key: "Discount Percentage",
                  name: "Discount Percentage",
                  type: "number",
                  customFooterTitle: "",
                  minColumnWidth: 150,
                  displayFullValue: true,
                  showTotal: false,
                  className: 'discount_pr',
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {item.discountedPercentage}%
                        </span>
                      </Stack>
                    );
                  },
                },
                {
                  fieldName: "discountedPrice",
                  isResizable: true,
                  key: "Discount Amount",
                  name: "Discount Amount",
                  type: "currency",
                  customFooterTitle: "",
                  displayFullValue: true,
                  minColumnWidth: 120,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.discountedPrice)}
                        </span>
                      </Stack>
                    );
                  },
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                },
                {
                  fieldName: "authorizedDiscount",
                  isResizable: true,
                  key: "Authorized Discount Amount",
                  name: "Authorized Discount Amount",
                  type: "currency",
                  customFooterTitle: "",
                  displayFullValue: true,
                  minColumnWidth: 200,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.authorizedDiscount)}
                        </span>
                      </Stack>
                    );
                  },
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                },
                {
                  fieldName: "unitPrice",
                  isResizable: true,
                  key: "unitBestPrice",
                  name: "Unit Best Price",
                  type: "currency",
                  customFooterTitle: "",
                  displayFullValue: true,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.unitPrice)}
                        </span>
                      </Stack>
                    );
                  },
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                },
                {
                  fieldName: `netAmount`,
                  isResizable: true,
                  key: "Extended Best Price",
                  name: "Extended Best Price",
                  type: "currency",
                  customFooterTitle: "",
                  displayFullValue: true,
                  minColumnWidth: 150,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.netAmount)}
                        </span>
                      </Stack>
                    );
                  },
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                },
                {
                  fieldName: `lineItemTax`,
                  isResizable: true,
                  key: "Line Tax (Extended Price * Tax rate)",
                  name: "",
                  minColumnWidth: 230,
                  type: "currency",
                  // Always show Total
                  //customFooterTitle: "Total",
                  // For Japan, don't show automatic sum since we'll manually render it
                  showTotal: quotationInfo.customer?.country !== "Japan",
                  displayFullValue: true,
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                  onRender(item: any) {
                    // First check if this is a totals row that needs special handling
                    const totalsRowContent = handleTotalsRowLineItemTax(item);
                    if (totalsRowContent) {
                      return totalsRowContent;
                    }
                    
                    // Regular cell rendering for individual items - right aligned
                    return (
                      <Stack verticalFill verticalAlign="center" horizontalAlign="end">
                        <span style={CustomSpanStyle}>
                          {formatPriceByCountry(item.lineItemTax)}
                        </span>
                      </Stack>
                    );
                  },
                  onRenderHeader(
                    props?: any,
                    defaultRender?: (props?: any) => JSX.Element | null
                  ) {
                    const taxType = quotationInfo.standaloneItems?.[0]?.taxType;
                    
                    // Simplified header for all countries
                    const headerContent = taxType ?
                      <div>Line {taxType} (Extended Price * {taxType} rate)</div> :
                      <div>Line Tax (Extended Price * Tax rate)</div>;
                    
                    return (
                      <div>
                        {headerContent}
                        {defaultRender && defaultRender(props)}
                      </div>
                    );
                  },
                },
                {
                  fieldName: `lineTotal`,
                  isResizable: true,
                  key: "Line Total Amount",
                  name: "Line Total Amount",
                  type: "currency",
                  customFooterTitle: "",
                  showTotal: quotationInfo.customer?.country !== "Japan",
                  displayFullValue: true,
                  onRender(item: any) {
                    // For Japan totals row, show quotationInfo.total instead of sum
                    if (quotationInfo.customer?.country === "Japan") {
                      const isTotalsRow = 
                        !item || 
                        (item && typeof item.lineTotal === 'undefined') || 
                        (item && item.productTitle === "Total") ||
                        (item && item.title === "Total") ||
                        (item && item.skuId === "total");
                      
                      if (isTotalsRow) {
                        const japanLineTotalAmount = calculateJapanLineTotalAmount();
                        return (
                          <Stack verticalFill verticalAlign="center" horizontalAlign="end">
                            <span style={CustomSpanStyle}>
                              {formatLineTaxTotalWithoutDecimals(quotationInfo.quoteCurrency, japanLineTotalAmount)}
                            </span>
                          </Stack>
                        );
                      }
                    }
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formatPriceByCountry(item.lineTotal)}
                        </span>
                      </Stack>
                    );
                  },
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                },
                {
                  fieldName: `discountReason`,
                  isResizable: true,
                  key: "Discount Reason",
                  name: "Discount Reason",
                  type: "string",
                  isMultiline: true,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {item.discountReason && item.discountReason.length > 0
                            ? item.discountReason
                            : "--"}
                        </span>
                      </Stack>
                    );
                  },
                },
              ]}
              selectionMode={SelectionMode.none}
              totalsRowTitleSettings={{
                title: "Total",
                tooltipMessage: "All total will appear here",
              }}
              setKey="ITEM_DETAILS_DATA_GRID_SORTABLE_SELECTABLE"
            />
          </div>
          
          {/* Add Japan tax total indicator below the grid for better visibility */}
          {quotationInfo.customer?.country === "Japan" && renderJapanTaxTotal()}
          {quotationInfo.customer?.country === "Japan" && renderJapanLineTotalAmount()}
        </Stack>
      </Card>
    );
  };

  const renderBundleItemDetailsComponent = (): React.ReactElement => {
    return (
      <Card>
        <Stack>
          <div
            style={{
              height: "475px",
            }}
          >
            <CoherenceDataGrid
              groups={bundle}
              groupProps={{ showEmptyGroups: false }}
              ariaLabelForSelectAllCheckbox="select all checkbox"
              checkButtonAriaLabel="checkbox"
              compactMode="compact"
              data={quotationInfo.bundleItems}
              onRenderDetailsHeader={onRenderDetailsHeader}
              onRenderRow={renderCustomRow}
              isScrollable
              listConfig={[
                {
                  fieldName: "sapItemId",
                  maxWidth: 100,
                  isResizable: true,
                  key: "skuId",
                  name: "SKU",
                  type: "string",
                },
                {
                  fieldName: "productTitle",
                  isResizable: true,
                  isMultiline: true,
                  key: "productName",
                  name: "Product Name",
                  type: "string",
                },
                {
                  fieldName: "availableInventory",
                  isResizable: true,
                  key: "availableInventory",
                  name: "Available Inventory",
                  type: "number",
                  customFooterTitle: "",
                  showTotal: false,
                  displayFullValue: true,
                  minColumnWidth: 150,
                },
                {
                  fieldName: "quantity",
                  isResizable: true,
                  key: "quantity",
                  name: "Quantity",
                  type: "number",
                  customFooterTitle: "",
                  displayFullValue: true,
                  isUpdated: true,
                },
                {
                  fieldName: "msrp",
                  isResizable: true,
                  key: "msrp",
                  name: "",
                  type: "number",
                  customFooterTitle: "",
                  showTotal: false,
                  displayFullValue: true,
                  onRender(item) {
                    taxType = quotationInfo.bundleItems?.[0].taxType || "";
                    return (

                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.msrp)}
                        </span>
                      </Stack>
                    );
                  },
                  onRenderHeader(
                    props?: any,
                    defaultRender?: (props?: any) => JSX.Element | null
                  ) {
                    //quotationInfo.bundleItems
                    const taxType = quotationInfo.bundleItems?.[0]?.taxType;
                    return taxType ?
                      (
                        <div> MSRP Ex {taxType}
                          {defaultRender && defaultRender(props)}
                        </div>
                      ) : (
                        <div>MSRP
                          {defaultRender && defaultRender(props)}
                        </div>
                      );


                  },
                },
                {
                  fieldName: "discountedPercentage",
                  isResizable: true,
                  key: "Discount Percentage",
                  name: "Discount Percentage",
                  type: "number",
                  customFooterTitle: "",
                  minColumnWidth: 150,
                  showTotal: false,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {item.discountedPercentage}%
                        </span>
                      </Stack>
                    );
                  },
                },
                {
                  fieldName: "isPreOrder",
                  isResizable: true,
                  key: "Is Pre Order",
                  name: "Is Pre Order",
                  type: "string",
                  customFooterTitle: "",
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {item.isPreOrder ? YES : NO}
                        </span>
                      </Stack>
                    );
                  },
                },
                {
                  fieldName: "discountedPrice",
                  isResizable: true,
                  key: "Discount Amount",
                  name: "Discount Amount",
                  type: "currency",
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                  customFooterTitle: "",
                  displayFullValue: true,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.discountedPrice)}
                        </span>
                      </Stack>
                    );
                  },
                },
                {
                  fieldName: "authorizedDiscount",
                  isResizable: true,
                  key: "Authorized Discount Amount",
                  name: "Authorized Discount Amount",
                  type: "currency",
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                  customFooterTitle: "",
                  displayFullValue: true,
                  minColumnWidth: 200,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.authorizedDiscount)}
                        </span>
                      </Stack>
                    );
                  },
                },
                {
                  fieldName: "unitPrice",
                  isResizable: true,
                  key: "unitBestPrice",
                  name: "Unit Best Price",
                  type: "currency",
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                  customFooterTitle: "",
                  displayFullValue: true,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.unitPrice)}
                        </span>
                      </Stack>
                    );
                  },
                },
                {
                  fieldName: `netAmount`,
                  isResizable: true,
                  key: "Extended Best Price",
                  name: "Extended Best Price",
                  type: "currency",
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                  customFooterTitle: "",
                  minColumnWidth: 150,
                  displayFullValue: true,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formattedPrice(item.netAmount)}
                        </span>
                      </Stack>
                    );
                  },
                },
                {
                  fieldName: `lineItemTax`,
                  isResizable: true,
                  key: "Line Tax (Extended Price * Tax rate)",
                  name: "",
                  type: "currency",
                  currencyCode() {
                    return quotationInfo.quoteCurrency;
                  },
                  // Always show Total
                  customFooterTitle: "Total",
                  // Always show total row
                  showTotal: true,
                  minColumnWidth: 230,
                  displayFullValue: true,
                  // Custom footer cell rendering handled in onRender and via the MutationObserver
                  onRender(item: any, index?: number, column?: any) {
                    // Check if this is a totals row - several approaches to detect it
                    const isTotalsRow = 
                      !item || 
                      (item && typeof item.lineItemTax === 'undefined') ||
                      (item && item.id === "total") ||
                      (item && item.key === "total") ||
                      (item && item.productTitle === "Total") ||
                      (item && item.title === "Total") ||
                      (item && item.skuId === "total");
                    
                    if (isTotalsRow) {
                      // Special case for Japan to show our custom calculated total
                      if (quotationInfo.customer?.country === "Japan") {
                        const japanTaxTotal = calculateJapanLineTaxTotal();
                        return (
                          <Stack verticalFill verticalAlign="center" horizontalAlign="end">
                            <span style={CustomSpanStyle}>
                              {formatLineTaxTotalWithoutDecimals(quotationInfo.quoteCurrency, japanTaxTotal)}
                            </span>
                          </Stack>
                        );
                      }
                      // For non-Japan countries, return undefined to use default sum behavior
                      return undefined;
                    }
                    
                    // Regular cell rendering for individual items - right aligned
                    return (
                      <Stack verticalFill verticalAlign="center" horizontalAlign="end">
                        <span style={CustomSpanStyle}>
                          {formatPriceByCountry(item.lineItemTax)}
                        </span>
                      </Stack>
                    );
                  },
                  onRenderHeader(
                    props?: any,
                    defaultRender?: (props?: any) => JSX.Element | null
                  ) {
                    const taxType = quotationInfo.bundleItems?.[0]?.taxType;
                    
                    // Simplified header for all countries
                    const headerContent = taxType ?
                      <div>Line {taxType} (Extended Price * {taxType} rate)</div> :
                      <div>Line Tax (Extended Price * Tax rate)</div>;
                    
                    return (
                      <div>
                        {headerContent}
                        {defaultRender && defaultRender(props)}
                      </div>
                    );
                  },
                },
                {
                  fieldName: `lineTotal`,
                  isResizable: true,
                  key: "Line Total Amount",
                  name: "Line Total Amount",
                  type: "currency",
                  customFooterTitle: "",
                  showTotal: quotationInfo.customer?.country !== "Japan",
                  displayFullValue: true,
                  currencyCode: () => quotationInfo?.quoteCurrency ?? "",
                  onRender(item: any) {
                    // For Japan totals row, show quotationInfo.total instead of sum
                    if (quotationInfo.customer?.country === "Japan") {
                      const isTotalsRow = 
                        !item || 
                        (item && typeof item.lineTotal === 'undefined') || 
                        (item && item.productTitle === "Total") ||
                        (item && item.title === "Total") ||
                        (item && item.skuId === "total");
                      
                      if (isTotalsRow) {
                        const japanLineTotalAmount = calculateJapanLineTotalAmount();
                        return (
                          <Stack verticalFill verticalAlign="center" horizontalAlign="end">
                            <span style={CustomSpanStyle}>
                              {formatLineTaxTotalWithoutDecimals(quotationInfo.quoteCurrency, japanLineTotalAmount)}
                            </span>
                          </Stack>
                        );
                      }
                    }
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {formatPriceByCountry(item.lineTotal)}
                        </span>
                      </Stack>
                    );
                  },
                },
                {
                  fieldName: `discountReason`,
                  isResizable: true,
                  key: "Discount Reason",
                  name: "Discount Reason",
                  type: "string",
                  isMultiline: true,
                  onRender(item) {
                    return (
                      <Stack verticalFill verticalAlign="center">
                        <span style={CustomSpanStyle}>
                          {item.discountReason && item.discountReason.length > 0
                            ? item.discountReason
                            : "--"}
                        </span>
                      </Stack>
                    );
                  },
                },
              ]}
              selectionMode={SelectionMode.none}
              totalsRowTitleSettings={{
                title: "Total",
                tooltipMessage: "All total will appear here",
              }}
              setKey="ITEM_DETAILS_DATA_GRID_SORTABLE_SELECTABLE"
            />
          </div>
          
          {/* Add Japan tax total indicator below the grid for better visibility */}
          {quotationInfo.customer?.country === "Japan" && renderJapanTaxTotal()}
          {quotationInfo.customer?.country === "Japan" && renderJapanLineTotalAmount()}
        </Stack>
      </Card>
    );
  };

  const rederDiscoutSummaryComponent = (): React.ReactElement => {
    return (
      <Stack>
        <Card>
          <div
            style={{
              height: "475px",
            }}
          >
            <CoherenceDataGrid
              ariaLabelForSelectAllCheckbox="select all checkbox"
              onRenderDetailsHeader={onRenderDetailsHeader}
              checkButtonAriaLabel="checkbox"
              compactMode="compact"
              data={
                discountSummary?.discountedLineItems
                  ? discountSummary.discountedLineItems
                  : []
              }
              isLoading={discountTableLoading}
              isScrollable
              listConfig={
                quotationInfo.customer.region === "EMEA"
                  ? customListConfig
                  : customListConfig.filter((x) => x.key !== "levyFees")
              }
              selectionMode={SelectionMode.none}
              setKey="DISCOUNT_SUMMARY_DATA_GRID_SORTABLE_SELECTABLE"
            />
          </div>
          {discountSummary && (
            <Stack
              horizontal
              horizontalAlign="end"
            >
              <Stack
                verticalFill
                verticalAlign="start"
                style={
                  props.windowSize.width <= 650
                    ? { width: "100%" }
                    : { width: "350px" }
                }
              >
                <Stack horizontal horizontalAlign="space-between">
                  <span style={CustomSpanStyle}>
                    <strong>Total Cart MSRP Ex {discountSummary?.discountedLineItems?.[0]?.taxType}</strong>
                  </span>
                  <span style={CustomSpanStyle}>
                    {priceWithCurrency(
                      quotationInfo?.quoteCurrency,
                      discountSummary.totalMSRP
                    )}
                  </span>
                </Stack>
                <Stack horizontal horizontalAlign="space-between">
                  <span style={CustomSpanStyle}>
                    <strong>Total Cart Discount Percentage</strong>
                  </span>
                  <span style={CustomSpanStyle}>
                    {discountSummary?.totalDiscountPercent}%
                  </span>
                </Stack>
                <Stack horizontal horizontalAlign="space-between">
                  <span style={CustomSpanStyle}>
                    <strong>Total Cart Discount Amount</strong>
                  </span>
                  <span style={CustomSpanStyle}>
                    {priceWithCurrency(
                      quotationInfo.quoteCurrency,
                      discountSummary?.totalDiscountAmount
                    )}
                  </span>
                </Stack>
                <Stack horizontal horizontalAlign="space-between">
                  <span style={CustomSpanStyle}>
                    <strong>Total Cart Discounted Price</strong>
                  </span>
                  <span style={CustomSpanStyle}>
                    {priceWithCurrency(
                      quotationInfo.quoteCurrency,
                      discountSummary?.totalDiscountPrice
                    )}
                  </span>
                </Stack>
                {quotationInfo.customer.region === "EMEA" && (
                  <Stack horizontal horizontalAlign="space-between">
                    <span style={CustomSpanStyle}>
                      <strong>Total Levy Fee</strong>
                    </span>
                    <span style={CustomSpanStyle}>
                      {priceWithCurrency(
                        quotationInfo.quoteCurrency,
                        discountSummary?.totalFeeAmount
                      )}
                    </span>
                  </Stack>
                )}
                <Stack horizontal horizontalAlign="space-between">
                  <span style={CustomSpanStyle}>
                    <strong>Total {taxType}</strong>
                  </span>
                  <span style={CustomSpanStyle}>
                    {priceWithCurrency(
                      quotationInfo.quoteCurrency,
                      discountSummary?.totalTax
                    )}
                  </span>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Card>
      </Stack>
    );
  };
  const onPivotSelectionChange = (
    item?: PivotItem,
    ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    if (item?.props.headerText) {
      appInsights.trackEvent({
        name: `In Quotation.tsx, Clicked Pivot ${item.props.headerText} to filter and show quotations of selected status.`,
      });
      setSelectedPivot(item.props.headerText);
    }
  };

  return (
    <Stack>
      {(quotationInfo.standaloneItems.length > 0 ||
        quotationInfo.bundleItems.length > 0) && (
          <Pivot aria-label={ORDER_DETAILS} onLinkClick={onPivotSelectionChange}>
            <PivotItem headerText={ORDER_DETAILS}></PivotItem>
            <PivotItem headerText={PRICING_SUMMARY}></PivotItem>
          </Pivot>
        )}
      {(quotationInfo.standaloneItems.length > 0 ||
        quotationInfo.bundleItems.length > 0) &&
        selectedPivot === ORDER_DETAILS && (
          <Stack>
            <Stack>{renderPivots()}</Stack>
            <Stack>
              {detailSelectedPivot === DeviceType.Standalone &&
                quotationInfo.standaloneItems.length > 0 && (
                  <Stack>
                    {renderStandaloneItemDetailsComponent()}
                  </Stack>
                )}
              {detailSelectedPivot === DeviceType.Bundle &&
                quotationInfo.bundleItems.length > 0 && (
                  <Stack>
                    {renderBundleItemDetailsComponent()}
                  </Stack>
                )}
            </Stack>
          </Stack>
        )}
      {selectedPivot === PRICING_SUMMARY && discountTableLoading && (
        <Stack
          style={{
            height: "200px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingState primaryText="Loading Pricing Summary" />
        </Stack>
      )}
      {(quotationInfo.standaloneItems.length > 0 ||
        quotationInfo.bundleItems.length > 0) &&
        selectedPivot === PRICING_SUMMARY &&
        !discountTableLoading && (
          <Stack>{rederDiscoutSummaryComponent()}</Stack>
        )}
      {selectedPivot === PRICING_SUMMARY &&
        !discountTableLoading &&
        ((detailSelectedPivot === DeviceType.Bundle &&
          quotationInfo.bundleItems.length === 0) ||
          (detailSelectedPivot === DeviceType.Standalone &&
            quotationInfo.standaloneItems.length === 0)) && (
          <Stack horizontalAlign="center" style={{ marginTop: "10vw" }}>
            <h4>No Record Found</h4>
          </Stack>
        )}
    </Stack>
  );
};

export default ItemComponent;
